import { useState } from "react";
import { ActionStrip } from "./ActionStrip";
import { Avatar } from "./Avatar";
import { href } from "../lib/route";
import { REVIEWING_HINT, REVIEWING_TITLE, reviewingRows } from "../lib/reviewerSeat";
import { acceptLink, endLink, useReviewerLinks } from "../routes/useReviewer";

/* THE `Reviewing` GROUP (reviewer seat step 2, 2026-10-10; brief § "Where each side sees it": *"the client appears under a
 * `Reviewing` group, not among his clients (no assign, no builder, no tasks, no chat)"*). It sits on the Clients page
 * UNDER his own clients' table — its own heading, its own rows — and draws NOTHING for an account no client has invited
 * (`reviewer_links()` holds no reviewer/invitee row): no empty heading for every coach who will never review anyone.
 * A fourth sidebar section is HIS decision (`nav.ts`), so the group is a part of the Clients page, not a section.
 * Accept / Leave are the seat's own RPCs (`reviewer_accept`, `reviewer_end`); Open goes to the reviewer's page. */
export function ReviewingGroup() {
  const { state, reload } = useReviewerLinks();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (state.status !== "ready") {
    // A failed read says so; a loading one draws nothing (the group usually does not exist).
    return state.status === "error" ? <p className="ht-caption reviewing-error" role="alert">Could not load the clients you review: {state.message}</p> : null;
  }
  const rows = reviewingRows(state.data);
  if (rows.length === 0) return null;
  const act = (id: string, f: (id: string) => Promise<string | null>, confirm?: string) => {
    if (confirm !== undefined && !window.confirm(confirm)) return;
    setBusy(id); setError(null);
    void f(id).then((e) => { setBusy(null); if (e !== null) setError(e); else reload(); });
  };
  return (
    <section className="reviewing-group" aria-labelledby="reviewing-title">
      <header className="reviewing-head">
        <h2 id="reviewing-title" className="reviewing-title">{REVIEWING_TITLE}</h2>
        <p className="ours-caption">{REVIEWING_HINT}</p>
      </header>
      <table className="ours-rows roster-rows reviewing-rows">
        <caption className="ht-visually-hidden">Clients you review</caption>
        <tbody>
          {rows.map((r) => (
            <tr key={r.linkId} className={r.state === "invited" ? "ours-row is-invited" : "ours-row"}>
              <th scope="row" className="ours-who-cell"><div className="ours-who">
                <Avatar name={r.name} photoUrl={null} className="ours-disc" />
                <span className="ours-names">
                  {r.state === "active"
                    ? <a className="ours-name" href={href({ name: "reviewing", clientId: r.clientId })}>{r.name}</a>
                    : <span className="ours-name">{r.name}</span>}
                  {r.state === "invited" && <span className="ours-caption">Asks you to review their training</span>}
                </span>
              </div></th>
              <td className="ours-actions">
                <ActionStrip label={`Actions for ${r.name}`} items={r.state === "invited"
                  ? [
                    { key: "decline", label: "Decline", destructive: true, disabled: busy !== null, onClick: () => act(r.linkId, endLink, `Decline ${r.name}'s invitation?`) },
                    { key: "accept", label: busy === r.linkId ? "Accepting…" : "Accept", disabled: busy !== null, onClick: () => act(r.linkId, acceptLink) },
                  ]
                  : [
                    { key: "leave", label: "Leave", destructive: true, disabled: busy !== null,
                      onClick: () => act(r.linkId, endLink, `Stop reviewing ${r.name}? Your sent reviews stay with them.`) },
                    { key: "open", label: "Open", href: href({ name: "reviewing", clientId: r.clientId }) },
                  ]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error !== null && <p className="signin-form-error" role="alert">{error}</p>}
    </section>
  );
}
